import { CSSProperties, useEffect, useState } from 'react';
import { useAssistantApi } from '@assistant-ui/react';

import promptSuggestion from '@atom/images/promptSuggestion.svg';
import { Chip } from '@atom/mui';
import colors from '@atom/styles/colors';
import { SuspendedToolCallCandidate } from '@atom/types/ai';

import { useSuspendedPicker } from './hooks/useSuspendedPicker';
import { useChatContext } from './ChatContext';

import classes from './chat.module.css';

const styles: Record<string, CSSProperties> = {
  chip: {
    background: colors.utility.highlight,
    borderRadius: '20px',
  },
};

const ChatSuspendedPicker = () => {
  const { suspended, isLast } = useSuspendedPicker();
  const { setResume } = useChatContext();
  const api = useAssistantApi();
  const [chosenId, setChosenId] = useState<string | null>(null);

  const suspendedRunId = suspended?.runId;
  const suspendedToolName = suspended?.toolName;

  useEffect(() => {
    if (suspendedRunId && suspendedToolName && isLast) {
      setResume({
        runId: suspendedRunId,
        toolName: suspendedToolName,
        resumeData: { message: '' },
      });
    }
  }, [suspendedRunId, suspendedToolName, isLast, setResume]);

  if (!suspended) {
    return null;
  }

  const disabled = !isLast || chosenId !== null;

  const onPick = (candidate: SuspendedToolCallCandidate) => {
    if (disabled) {
      return;
    }

    setChosenId(candidate.id);

    setResume({
      runId: suspended.runId,
      toolName: suspended.toolName,
      resumeData: {
        candidateId: candidate.id,
        message: candidate.name,
      },
    });

    api.thread().append({
      role: 'user',
      content: [{ type: 'text', text: candidate.name }],
    });
  };

  return (
    <div className={classes.suspendedPickerContainer}>
      <div className={classes.suspendedPickerPrompt}>
        {suspended.suspendPayload.prompt}
      </div>
      <div className={classes.suspendedPickerPillRow}>
        {suspended.suspendPayload.candidates.map(candidate => {
          const label = (
            <div className={classes.suspendedPickerPillLabel}>
              <img
                src={promptSuggestion}
                alt=""
                className={classes.suspendedPickerPillArrow}
              />
              <span className={classes.suspendedPickerPillText}>
                {candidate.name}
              </span>
            </div>
          );

          return (
            <Chip
              key={candidate.id}
              label={label}
              onClick={disabled ? undefined : () => onPick(candidate)}
              style={styles.chip}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ChatSuspendedPicker;
